import type {Participant, ParticipantID} from '@pexip/infinity';
import uFuzzy from '@leeoniya/ufuzzy';

export class ParticipantSearcher {
    /**
     * Searches for participants in the given list based on the provided search query.
     * @param participants The list of participants to search.
     * @param searchQuery The search filter string.
     * @returns An array of search results for each participant.
     */
    search(participants: Participant[], searchQuery: string): SearchResult {
        const result = new Map<
            ParticipantID,
            {
                participantId: ParticipantID;
                matches: {start: number; end: number; score?: number}[];
            }
        >();

        if (!searchQuery) {
            for (const item of participants) {
                result.set(item.uuid, {
                    participantId: item.uuid,
                    matches: [],
                });
            }
            return result;
        }

        const names = participants.map(item =>
            uFuzzy.latinize(item.overlayText ?? item.displayName ?? ''),
        );

        const uf = new uFuzzy({
            unicode: true,
            interSplit: "[^\\p{L}\\d']+",
            intraSplit: '\\p{Ll}\\p{Lu}',
            intraBound: '\\p{L}\\d|\\d\\p{L}|\\p{Ll}\\p{Lu}',
            intraChars: "[\\p{L}\\d']",
            intraContr: "'\\p{L}{1,2}\\b",
        });

        const latinizedSearchQuery = uFuzzy.latinize(searchQuery);
        const idxs = uf.filter(names, latinizedSearchQuery);
        if (!idxs || idxs.length === 0) {
            return result;
        }

        const info = uf.info(idxs, names, latinizedSearchQuery);
        const order = uf.sort(info, names, latinizedSearchQuery);

        for (const orderIdx of order) {
            const infoIdx = info.idx[orderIdx];
            if (infoIdx === undefined) {
                continue;
            }
            const ranges = info.ranges[orderIdx] || [];
            const participant = participants[infoIdx];
            if (!participant) {
                continue;
            }
            const matches =
                ranges.length === 2 &&
                ranges[0] !== undefined &&
                ranges[1] !== undefined
                    ? [{start: ranges[0], end: ranges[1] - 1}]
                    : [];
            result.set(participant.uuid, {
                participantId: participant.uuid,
                matches,
            });
        }

        return result;
    }
}

/**
 * Represents the results of a search operation for participants.
 *
 * The `SearchResult` is a `Map` where each key is a `ParticipantID`, and the value is an object
 * containing:
 *   - `participantId`: The ID of the participant.
 *   - `matches`: An array of match objects, each describing a segment of interest with:
 *       - `start`: The starting index of the match.
 *       - `end`: The ending index of the match.
 *       - `score` (optional): A numerical score indicating the relevance or quality of the match.
 */
export type SearchResult = Map<
    ParticipantID,
    {
        participantId: ParticipantID;
        matches: {start: number; end: number; score?: number}[];
    }
>;
